import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Switch,
  Divider,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { FiMail } from 'react-icons/fi';

// Cast to React.ElementType to fix TypeScript error
const FiMailIcon = FiMail as React.ElementType;

export interface EmailSettings {
  enabled: boolean;
  sendConfirmation: boolean;
  sendReminders: boolean;
  sendNewBookingNotifications: boolean;
  newBookingNotificationEmail: string;
  tokenExpirationDays: number;
  reminder24Hours: boolean;
  reminder2Hours: boolean;
  webAppUrl: string;
}

const DEFAULT_EMAIL_SETTINGS: EmailSettings = {
  enabled: true,
  sendConfirmation: true,
  sendReminders: true,
  sendNewBookingNotifications: true,
  newBookingNotificationEmail: 'support@glisten-ecoshine.com',
  tokenExpirationDays: 30,
  reminder24Hours: true,
  reminder2Hours: true,
  webAppUrl: 'https://glisten-app.web.app',
};

const EmailSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<EmailSettings>(DEFAULT_EMAIL_SETTINGS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'email');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as EmailSettings;
        setSettings({
          enabled: data.enabled !== false,
          sendConfirmation: data.sendConfirmation !== false,
          sendReminders: data.sendReminders !== false,
          sendNewBookingNotifications: data.sendNewBookingNotifications !== false,
          newBookingNotificationEmail: data.newBookingNotificationEmail || 'support@glisten-ecoshine.com',
          tokenExpirationDays: data.tokenExpirationDays || 30,
          reminder24Hours: data.reminder24Hours !== false,
          reminder2Hours: data.reminder2Hours !== false,
          webAppUrl: data.webAppUrl || 'https://glisten-app.web.app',
        });
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load email settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EmailSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate email format
    if (settings.newBookingNotificationEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(settings.newBookingNotificationEmail)) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a valid email address',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // Validate URL format
    if (settings.webAppUrl) {
      try {
        new URL(settings.webAppUrl);
      } catch {
        toast({
          title: 'Validation Error',
          description: 'Please enter a valid URL',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'email');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          ...settings,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          ...settings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Email settings updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save email settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_EMAIL_SETTINGS);
    toast({
      title: 'Settings Reset',
      description: 'Settings have been reset to defaults',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Box>
        <Text>Loading email settings...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={6} align="center">
        <FiMailIcon size={24} />
        <Heading size="lg">Email Settings</Heading>
      </HStack>

      <Alert status="info" mb={6} borderRadius="md">
        <AlertIcon />
        Configure email notification settings for booking confirmations, reminders, and new booking notifications.
      </Alert>

      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* General Settings */}
            <Box>
              <Heading size="md" mb={4}>General Settings</Heading>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>Enable Email Notifications</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Master switch for all email notifications
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.enabled}
                      onChange={(e) => handleChange('enabled', e.target.checked)}
                      colorScheme="green"
                    />
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>Web App URL</FormLabel>
                  <Input
                    value={settings.webAppUrl}
                    onChange={(e) => handleChange('webAppUrl', e.target.value)}
                    placeholder="https://glisten-app.web.app"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Base URL for links in email notifications
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Token Expiration Days</FormLabel>
                  <Input
                    type="number"
                    value={settings.tokenExpirationDays}
                    onChange={(e) => handleChange('tokenExpirationDays', parseInt(e.target.value) || 30)}
                    min={1}
                    max={365}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Number of days before guest booking access tokens expire
                  </Text>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Booking Confirmation Emails */}
            <Box>
              <Heading size="md" mb={4}>Booking Confirmation Emails</Heading>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>Send Confirmation Emails</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Send confirmation emails to guest users when bookings are created
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.sendConfirmation}
                      onChange={(e) => handleChange('sendConfirmation', e.target.checked)}
                      colorScheme="green"
                      isDisabled={!settings.enabled}
                    />
                  </HStack>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Booking Reminder Emails */}
            <Box>
              <Heading size="md" mb={4}>Booking Reminder Emails</Heading>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>Send Reminder Emails</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Send reminder emails to guest users before scheduled appointments
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.sendReminders}
                      onChange={(e) => handleChange('sendReminders', e.target.checked)}
                      colorScheme="green"
                      isDisabled={!settings.enabled}
                    />
                  </HStack>
                </FormControl>

                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>24-Hour Reminder</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Send reminder 24 hours before scheduled appointment
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.reminder24Hours}
                      onChange={(e) => handleChange('reminder24Hours', e.target.checked)}
                      colorScheme="green"
                      isDisabled={!settings.enabled || !settings.sendReminders}
                    />
                  </HStack>
                </FormControl>

                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>2-Hour Reminder</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Send reminder 2 hours before scheduled appointment
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.reminder2Hours}
                      onChange={(e) => handleChange('reminder2Hours', e.target.checked)}
                      colorScheme="green"
                      isDisabled={!settings.enabled || !settings.sendReminders}
                    />
                  </HStack>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* New Booking Notifications */}
            <Box>
              <Heading size="md" mb={4}>New Booking Notifications</Heading>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <HStack justify="space-between">
                    <Box>
                      <FormLabel mb={1}>Send New Booking Notifications</FormLabel>
                      <Text fontSize="sm" color="gray.600">
                        Send email notifications when new bookings are created
                      </Text>
                    </Box>
                    <Switch
                      isChecked={settings.sendNewBookingNotifications}
                      onChange={(e) => handleChange('sendNewBookingNotifications', e.target.checked)}
                      colorScheme="green"
                      isDisabled={!settings.enabled}
                    />
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>Notification Email Address</FormLabel>
                  <Input
                    value={settings.newBookingNotificationEmail}
                    onChange={(e) => handleChange('newBookingNotificationEmail', e.target.value)}
                    placeholder="support@glisten-ecoshine.com"
                    type="email"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Email address to receive notifications when new bookings are created
                  </Text>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Actions */}
            <HStack justify="flex-end" spacing={4}>
              <Button onClick={handleReset} variant="outline">
                Reset to Defaults
              </Button>
              <Button
                onClick={handleSave}
                colorScheme="green"
                isLoading={saving}
                loadingText="Saving..."
              >
                Save Settings
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default EmailSettingsComponent;

